import { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import CardFormContainer from './components/CardFormContainer';
import { OrderContext } from './types/CardData';
import { postMessageService } from './services/postMessageService';
import { apiService } from './services/apiService';
import { InitWidgetPayload, SubmitButtonConfig } from './types/PostMessageEvent';

// Type definition for runtime configuration
interface CardVaultConfig {
  allowedOrigins?: string;
  apiBaseUrl?: string;
}

// Extend Window interface to include CARDVAULT_CONFIG
declare global {
  interface Window {
    CARDVAULT_CONFIG?: CardVaultConfig;
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    error: {
      main: '#d32f2f',
    },
    background: {
      default: 'transparent',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'transparent',
        },
      },
    },
  },
});

function App() {
  const [orderContext, setOrderContext] = useState<OrderContext | null>(null);
  const [bearerToken, setBearerToken] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState<string | null>(null);  
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [submitButtonConfig, setSubmitButtonConfig] = useState<SubmitButtonConfig | undefined>(undefined);

  useEffect(() => {
    // Listen for INIT_WIDGET message from parent (Phase 3: includes bearer token)
    postMessageService.on<InitWidgetPayload>('INIT_WIDGET', async (payload) => {
      console.log('[App] Received INIT_WIDGET from parent window');

      // Set the API URL first so token validation hits the correct endpoint.
      apiService.setApiUrl(payload.apiUrl);
      apiService.setKongApiUrl(payload.kongBaseUrl);
      console.log('[App] Kong_API URL set to:', payload.kongBaseUrl);
      // Validate the bearer token against the API before rendering the form.
      // Backend is the authoritative validator for signature/claims/expiry.
      let isValid: boolean;
      try {
        isValid = await apiService.validateBearerToken(payload.bearerToken);
      } catch {
        // Network error or 5xx — auth service is unreachable; not a token validity judgment
        console.error('[App] Auth service unavailable during token validation');
        postMessageService.sendError(
          'Authentication service is temporarily unavailable. Please try again.',
          'AUTH_ERROR'
        );
        return;
      }
      if (!isValid) {
        console.error('[App] Bearer token validation failed');
        postMessageService.sendError(
          'Authentication failed: Token is expired or invalid',
          'INVALID_TOKEN'
        );
        return;
      }

      // Store bearer token, API URL, and optional correlation ID for API calls
      setBearerToken(payload.bearerToken);
      setApiUrl(payload.apiUrl);      
      setCorrelationId(payload.correlationId ?? null);
      console.log('[App] Kong URL set to:', payload.kongBaseUrl);
      // Store submit button configuration if provided
      if (payload.submitButton) {
        console.log('[App] Submit button config received:', JSON.stringify(payload.submitButton, null, 2));
        console.log('[App] show value:', payload.submitButton.show);
        console.log('[App] show type:', typeof payload.submitButton.show);
        setSubmitButtonConfig(payload.submitButton);
      } else {
        console.log('[App] No submit button config provided - using defaults');
        setSubmitButtonConfig(undefined); // Will use defaults in CardFormContainer
      }

      // Store order context to display form
      setOrderContext({
        orderId: payload.orderContext.orderId,
        amount: payload.orderContext.amount,
        currency: payload.orderContext.currency,
        vendorId: payload.orderContext.vendorId,
        customerId: payload.orderContext.customerId,
        propertyId: payload.orderContext.propertyId,
        excludeCards: Array.isArray(payload.orderContext.excludeCards)
          ? payload.orderContext.excludeCards
          : undefined,
      });

      console.log('[App] Widget initialized with authentication');
      // WIDGET_INITIALIZED is sent by CardFormContainer after it mounts
      // and registers its SUBMIT_CARD_FORM listener — not here.
      // Sending it here would race: setOrderContext() triggers an async
      // re-render, so CardFormContainer's listener wouldn't exist yet.
    });

    // Backward compatibility: Listen for ORDER_CONTEXT message (legacy)
    // This can be removed once all integrations use INIT_WIDGET
    postMessageService.on<OrderContext>('ORDER_CONTEXT', (payload) => {
      console.log('[App] Received ORDER_CONTEXT from parent window (legacy mode)');
      setOrderContext({
        orderId: payload.orderId,
        amount: payload.amount,
        currency: payload.currency,
        vendorId: payload.vendorId,
        customerId: payload.customerId,
        propertyId: payload.propertyId,
      });
    });

    // Listen for CARD_FORM_CANCEL message from parent (triggered by Cancel button click)
    postMessageService.on<void>('CARD_FORM_CANCEL', () => {
      console.log('[App] Received CARD_FORM_CANCEL from parent window');
      // Reset form state
      setOrderContext(null);
    });

    // Set allowed origins (configure based on environment)
    // SECURITY: ALLOWED_ORIGINS is required - fail securely if not configured
    // Production: Read from runtime config.js (injected by Kubernetes ConfigMap)
    // Development: Read from VITE_ALLOWED_ORIGINS (.env file)
    let originsEnv: string;

    if (import.meta.env.DEV) {
      // Local development: Use VITE_ build-time variable from .env
      originsEnv = import.meta.env.VITE_ALLOWED_ORIGINS || '';
      console.log('[App] Development mode: Reading origins from VITE_ALLOWED_ORIGINS');
    } else {
      // Production: Read from runtime configuration
      const config = window.CARDVAULT_CONFIG;
      if (!config || !config.allowedOrigins) {
        console.error('[App] CRITICAL: Runtime configuration not found or ALLOWED_ORIGINS missing!');
        throw new Error(
          'Security configuration error: ALLOWED_ORIGINS must be set in runtime configuration. ' +
          'Check that config.js is loaded and ALLOWED_ORIGINS is configured in Kubernetes ConfigMap.'
        );
      }
      originsEnv = config.allowedOrigins;
      console.log('[App] Production mode: Reading origins from runtime config');
    }

    if (!originsEnv || originsEnv.trim() === '') {
      console.error('[App] CRITICAL: ALLOWED_ORIGINS is not configured!');
      throw new Error(
        'Security configuration error: ALLOWED_ORIGINS must be set. ' +
        'The widget cannot operate securely without origin validation.'
      );
    }

    const allowedOrigins = originsEnv.split(',').map(origin => origin.trim()).filter(origin => origin !== '');
    if (allowedOrigins.length === 0) {
      console.error('[App] CRITICAL: ALLOWED_ORIGINS contains no valid origins!');
      throw new Error(
        'Security configuration error: ALLOWED_ORIGINS must contain at least one valid origin.'
      );
    }

    postMessageService.setAllowedOrigins(allowedOrigins);
    console.info('[App] Origin validation enabled for:', allowedOrigins.length, 'origin(s)');

    // Notify parent window that the form is ready
    postMessageService.sendReady('1.0.0');

    // Don't destroy the singleton postMessageService on cleanup
    // as it's shared across the app and React StrictMode will
    // unmount/remount in development, breaking the event listener
    return () => {
      // Cleanup is not needed for singleton service
    };
  }, []);

  // Show loading state while waiting for order context from parent
  if (!orderContext) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Loading payment form...
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CardFormContainer
        orderContext={orderContext}
        bearerToken={bearerToken}
        apiUrl={apiUrl}
        correlationId={correlationId}
        submitButtonConfig={submitButtonConfig}
      />
    </ThemeProvider>
  );
}

export default App;
